import { useId } from "react";
import { useHudStore } from "../../store/hudStore";
import { FOLDER_ORDER } from "../../data/seed";
import { Button } from "../../design-system/components";
import type { VaultFile } from "../../types";
import "../../screens/character-vault.css";

// Controlled search keeps the input caret intact while note and folder buttons
// use the same target size, focus treatment, and spacing as other controls.
export default function VaultTree() {
  const st = useHudStore();
  const folderId = useId();
  const files: Record<string, VaultFile[]> = st.vaultLive && st.vaultTree ? st.vaultTree : {};
  const q = st.vq.trim().toLowerCase();
  const all: VaultFile[] = Object.values(files).flat();
  const hits = q ? all.filter((x) => x.path.toLowerCase().indexOf(q) > -1).slice(0, 12) : [];

  // A failed listing must not invent a tree of apparently empty folders.
  if (st.vaultFailed && !st.vaultLive) {
    return <div className="vault-tree-state" role="alert"><i aria-hidden="true" className="fa-solid fa-triangle-exclamation" /><strong>Vault listing unavailable</strong><p>The vault listing didn't load. Your notes remain saved.</p></div>;
  }

  if (q) {
    return (
      <div className="vault-tree-items" aria-label="Vault search results">
        {hits.map((h) => <Button key={h.path} variant="quiet" size="compact" className="vault-note-button" onClick={() => st.openNote(h.path)} aria-current={st.noteId === h.path ? "true" : undefined}>
          <span className="vault-note-copy"><span className="vault-note-name">{h.path}</span><span className="vault-note-meta">{h.meta}</span></span>
        </Button>)}
        {hits.length === 0 && <div className="vault-tree-state" role="status"><p>Nothing in the vault matches that.</p><Button variant="quiet" size="compact" onClick={() => st.setVq("")}>Clear search</Button></div>}
      </div>
    );
  }

  // The live vault supplies folders; the curated order only supplies familiar
  // ordering and icons. New top-level folders appear without a code change.
  const iconOf = new Map(FOLDER_ORDER);
  const present = Object.keys(files);
  const folderNames = present.length
    ? [...FOLDER_ORDER.map(([n]) => n).filter((n) => present.includes(n)), ...present.filter((n) => !iconOf.has(n)).sort()]
    : FOLDER_ORDER.map(([n]) => n);

  return (
    <div className="vault-tree-items" aria-label="Vault folders">
      {folderNames.map((name, index) => {
        const fa = iconOf.get(name) ?? "fa-solid fa-folder";
        const list = files[name] || [];
        const open = !!st.vaultOpen[name];
        const filesId = `${folderId}-${index}`;
        return <div key={name}>
          <Button variant="quiet" size="compact" className="vault-folder" aria-expanded={open} aria-controls={open ? filesId : undefined} onClick={() => st.toggleFolder(name)} icon={open ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"}>
            <span className="vault-folder-content"><i aria-hidden="true" className={fa} /><span className="vault-folder-name">{name}</span><span className="vault-folder-count">{list.length}{list.length === 1 ? " note" : " notes"}</span></span>
          </Button>
          {open && <div className="vault-folder-files" id={filesId}>
            {list.length > 0 ? list.map((x) => <Button key={x.path} variant="quiet" size="compact" className="vault-note-button" onClick={() => st.openNote(x.path)} aria-current={st.noteId === x.path ? "true" : undefined}>
              <span className="vault-note-copy"><span className="vault-note-name">{x.path.split("/").slice(1).join("/")}</span><span className="vault-note-meta">{x.meta}</span></span>
            </Button>) : <p className="vault-folder-empty">Empty. Nothing here yet.</p>}
          </div>}
        </div>;
      })}
    </div>
  );
}
